type EventMap = HTMLElementEventMap;

export interface ElementOptions<K extends keyof HTMLElementTagNameMap> {
  class?: string;
  text?: string;
  html?: string;
  attrs?: Record<string, string | number | boolean | undefined>;
  on?: { [E in keyof EventMap]?: (this: HTMLElementTagNameMap[K], ev: EventMap[E]) => void };
  children?: (HTMLElement | string | null | undefined)[];
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  opts: ElementOptions<K> = {},
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (opts.class) node.className = opts.class;
  if (opts.text !== undefined) node.textContent = opts.text;
  if (opts.html !== undefined) node.innerHTML = opts.html;
  if (opts.attrs) {
    for (const [k, v] of Object.entries(opts.attrs)) {
      if (v === undefined || v === false) continue;
      if (v === true) node.setAttribute(k, '');
      else node.setAttribute(k, String(v));
    }
  }
  if (opts.on) {
    for (const [evName, handler] of Object.entries(opts.on)) {
      if (handler) node.addEventListener(evName, handler as EventListener);
    }
  }
  if (opts.children) {
    for (const child of opts.children) {
      if (child == null) continue;
      node.append(typeof child === 'string' ? document.createTextNode(child) : child);
    }
  }
  return node;
}

export function clear(node: HTMLElement): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function mount(parent: HTMLElement, children: HTMLElement[]): void {
  clear(parent);
  for (const c of children) parent.append(c);
}

export function formatRelativeTime(iso: string | number): string {
  const date = typeof iso === 'number' ? new Date(iso) : new Date(iso);
  const diffSec = (Date.now() - date.getTime()) / 1000;
  const abs = Math.abs(diffSec);
  if (abs < 60) return 'just now';
  if (abs < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (abs < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  if (abs < 30 * 86400) return `${Math.round(diffSec / 86400)}d ago`;
  return date.toLocaleDateString();
}

export function formatDateTime(value: number | string): string {
  const date = typeof value === 'number' ? new Date(value) : new Date(value);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDuration(startedAt: number, finishedAt: number | null): string {
  const end = finishedAt ?? Date.now();
  const ms = end - startedAt;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

export type PillKind =
  | 'idle'
  | 'running'
  | 'success'
  | 'failed'
  | 'timeout'
  | 'warning';

export function statusPill(kind: PillKind, label?: string, opts: { large?: boolean } = {}): HTMLElement {
  return el('span', {
    class: `pill ${kind}${opts.large ? ' lg' : ''}`,
    text: label ?? kind,
  });
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '–';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  const decimals = v >= 100 || i === 0 ? 0 : v >= 10 ? 1 : 2;
  return `${v.toFixed(decimals)} ${units[i]}`;
}

export function formatPercent(value: number, decimals = 1): string {
  if (!Number.isFinite(value)) return '–';
  return `${value.toFixed(decimals)}%`;
}

export function formatUptime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '–';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
